import {
  EnterpriseRatingSeverity,
  EnterpriseRatingSeverityDefault,
  IUnderwritingQuote,
  RaterPolicyDetails,
  RaterUserQuotes,
  Submission,
} from '@converge/graphql';
import {
  IRaterContextState,
  IRaterEnterpriseFusionScoreProps,
  IRaterProps,
  IRaterScheduleRatingProps,
  RaterColumnCount,
  RaterTab,
} from '../types';
import {
  RISK_LEVEL_TYPE_DEFAULT,
  RiskLevelType,
  UnderwritingEnterpriseFusionScoreType,
  UnderwritingEnterpriseFusionScoreTypeId,
  UnderwritingScheduleRatingType,
  UnderwritingScheduleRatingTypeId,
} from '@converge/data';

export const defaultScheduleRatingData = (): IRaterScheduleRatingProps[] => [
  {
    ratingTypeId: UnderwritingScheduleRatingTypeId.AdditionalInsureds,
    ratingType: UnderwritingScheduleRatingType.AdditionalInsureds,
    minimum: -25,
    maximum: 25,
    selectedRating: 10,
    notes: '',
  },
  {
    ratingTypeId: UnderwritingScheduleRatingTypeId.YearsInBusiness,
    ratingType: UnderwritingScheduleRatingType.YearsInBusiness,
    minimum: -25,
    maximum: 25,
    selectedRating: 0,
    notes: '',
  },
  {
    ratingTypeId: UnderwritingScheduleRatingTypeId.TerritoryOfOperations,
    ratingType: UnderwritingScheduleRatingType.TerritoryOfOperations,
    minimum: -25,
    maximum: 25,
    selectedRating: 0,
    notes: '',
  },
  {
    ratingTypeId: UnderwritingScheduleRatingTypeId.OrganizationalSohistication,
    ratingType: UnderwritingScheduleRatingType.OrganizationalSohistication,
    minimum: -25,
    maximum: 25,
    selectedRating: 0,
    notes: '',
  },
  {
    ratingTypeId:
      UnderwritingScheduleRatingTypeId.SignificantTermsAndConditions,
    ratingType: UnderwritingScheduleRatingType.SignificantTermsAndConditions,
    minimum: -25,
    maximum: 25,
    selectedRating: 0,
    notes: '',
  },
];

export const defaultEnterpriseRatingRiskLevels =
  (): IRaterEnterpriseFusionScoreProps[] => [
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.CentralizedPoliciesProcedures,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.CentralizedPoliciesProcedures,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId: UnderwritingEnterpriseFusionScoreTypeId.FinancialConditions,
      scoreType: UnderwritingEnterpriseFusionScoreType.FinancialConditions,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId: UnderwritingEnterpriseFusionScoreTypeId.NatureofOperations,
      scoreType: UnderwritingEnterpriseFusionScoreType.NatureofOperations,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.QualityofServiceProviderContracts,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.QualityofServiceProviderContracts,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.QualityofServiceProviders,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.QualityofServiceProviders,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.InvestmentInRiskManagement,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.InvestmentInRiskManagement,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.ClaimsExperienceLastThreeYears,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.ClaimsExperienceLastThreeYears,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.UnusualRegulatoryScrutiny,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.UnusualRegulatoryScrutiny,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.RiskAssociatedWithBusinessLocations,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.RiskAssociatedWithBusinessLocations,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
    {
      scoreTypeId:
        UnderwritingEnterpriseFusionScoreTypeId.CoverageVarianceFromBasePolicy,
      scoreType:
        UnderwritingEnterpriseFusionScoreType.CoverageVarianceFromBasePolicy,
      riskLevel: RISK_LEVEL_TYPE_DEFAULT,
      notes: '',
    },
  ];

export const riskLevelToEnterpriseRatingSeverity = (
  riskLevel: RiskLevelType
): EnterpriseRatingSeverity => {
  switch (riskLevel) {
    case RiskLevelType.VeryLow:
      return EnterpriseRatingSeverity.VeryLow;
    case RiskLevelType.Low:
      return EnterpriseRatingSeverity.Low;
    case RiskLevelType.Moderate:
      return EnterpriseRatingSeverity.Moderate;
    case RiskLevelType.High:
      return EnterpriseRatingSeverity.High;
    case RiskLevelType.VeryHigh:
      return EnterpriseRatingSeverity.VeryHigh;
    default:
      return EnterpriseRatingSeverityDefault;
  }
};

export enum UnderwritingEnterpriseFusionScoreTypeUuid {
  CentralizedPoliciesProcedures = '800693ad-7a57-446f-a5a0-98aeab26aec0',
  FinancialConditions = 'f9dae66d-3f1d-4fce-a686-a744f80b52c7',
  NatureofOperations = '0ff133ab-2531-43f4-bd84-f33c965f3d59',
  QualityofServiceProviderContracts = '3709eeae-9b20-46ec-9fce-eceff9989077',
  QualityofServiceProviders = '3c3d7a36-a02f-4579-bd2d-814fcb31eea3',
  InvestmentInRiskManagement = '46e3e88c-13e1-4bfe-bbb1-a83ee74f2a65',
  ClaimsExperienceLastThreeYears = 'f88ca21b-e3e9-4d54-a5f6-955309602a6d',
  UnusualRegulatoryScrutiny = '8a370724-9e4e-428e-805e-dfd8b78b681c',
  RiskAssociatedWithBusinessLocations = '77494659-11be-4469-b481-e85dd76b1005',
  CoverageVarianceFromBasePolicy = '003283d7-7472-4678-aec7-545246650785',
}

export const enterpriseRatingIdToUuid = (
  enterpriseRatingId: UnderwritingEnterpriseFusionScoreTypeId
): UnderwritingEnterpriseFusionScoreTypeUuid | string => {
  switch (enterpriseRatingId) {
    case UnderwritingEnterpriseFusionScoreTypeId.CentralizedPoliciesProcedures:
      return UnderwritingEnterpriseFusionScoreTypeUuid.CentralizedPoliciesProcedures;
    case UnderwritingEnterpriseFusionScoreTypeId.FinancialConditions:
      return UnderwritingEnterpriseFusionScoreTypeUuid.FinancialConditions;
    case UnderwritingEnterpriseFusionScoreTypeId.NatureofOperations:
      return UnderwritingEnterpriseFusionScoreTypeUuid.NatureofOperations;
    case UnderwritingEnterpriseFusionScoreTypeId.QualityofServiceProviderContracts:
      return UnderwritingEnterpriseFusionScoreTypeUuid.QualityofServiceProviderContracts;
    case UnderwritingEnterpriseFusionScoreTypeId.QualityofServiceProviders:
      return UnderwritingEnterpriseFusionScoreTypeUuid.QualityofServiceProviders;
    case UnderwritingEnterpriseFusionScoreTypeId.InvestmentInRiskManagement:
      return UnderwritingEnterpriseFusionScoreTypeUuid.InvestmentInRiskManagement;
    case UnderwritingEnterpriseFusionScoreTypeId.ClaimsExperienceLastThreeYears:
      return UnderwritingEnterpriseFusionScoreTypeUuid.ClaimsExperienceLastThreeYears;
    case UnderwritingEnterpriseFusionScoreTypeId.UnusualRegulatoryScrutiny:
      return UnderwritingEnterpriseFusionScoreTypeUuid.UnusualRegulatoryScrutiny;
    case UnderwritingEnterpriseFusionScoreTypeId.RiskAssociatedWithBusinessLocations:
      return UnderwritingEnterpriseFusionScoreTypeUuid.RiskAssociatedWithBusinessLocations;
    case UnderwritingEnterpriseFusionScoreTypeId.CoverageVarianceFromBasePolicy:
      return UnderwritingEnterpriseFusionScoreTypeUuid.CoverageVarianceFromBasePolicy;
    default:
      return 'unknown';
  }
};

export const ENTERPRISE_RATING_DATA = defaultEnterpriseRatingRiskLevels();
export const SCHEDULED_RATING_DATA = defaultScheduleRatingData();

export const createRaterData = (
  options: IRaterProps,
  quote: IUnderwritingQuote,
  submission: Submission,
  userQuotes: RaterUserQuotes,
  policy?: RaterPolicyDetails
): IRaterContextState => ({
  isAuthenticated: false,
  isRequestingRatingUpdate: false,
  lastMessageId: 0,
  messagesReceived: [],
  messagesSent: [],
  messagesToSend: [],
  mode: options.mode,
  options,
  quote: quote,
  submission,
  userQuotes,
  showExpandedView: false,
  columnCount: RaterColumnCount.Contracted,
  tab: RaterTab.CoverageRating,
  isEditable: options.isEditable ?? false,
  policy,
  expandCoverageListForExcessTowerLayerParticipants: [],
  upsertQuoteIsLoading: false,
  upsertAnswersIsLoading: false,
  endoresementAnswers: {},
  isRequestingUpsertQuoteNow: false,
  isRequestingUpsertQuoteAfterRating: false,
  changedFields: [],
});
